import { buildUnifiedProduct } from './fieldMapper.js';

/**
 * Generate llms.txt content
 * Built according to llmstxt.org specification
 * @param {Array} parsedData - Parsed CSV data
 * @param {string} baseUrl - Formatted base URL
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {string} - llms.txt content
 */
export function generateLlmsTxt(parsedData, baseUrl, platform = 'shopline') {
  const productSection = buildProductSection(parsedData, baseUrl, platform);
  const highValueSection = buildHighValueSection(baseUrl);
  const optionalSection = buildOptionalSection(baseUrl);
  const header = buildHeader(parsedData.length, platform);

  return `${header}\n\n${highValueSection}\n\n${productSection}\n\n${optionalSection}\n`;
}

/**
 * Build llms.txt header section
 * @param {number} productCount - Number of products
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {string} - Header section
 */
function buildHeader(productCount, platform) {
  const platformName = platform === 'shopify' ? 'Shopify' : 'SHOPLINE';
  return `# ${platformName} Product Catalog

> This file provides a curated index of ${platformName} product data for AI agents and search integration. It includes product entities structured per Schema.org/Product, with details on titles, prices, inventory, and more.

Important notes: Data is derived from ${platformName} CSV export. Use the linked NDJSON for machine-readable product data. Total products: ${productCount}.`;
}

/**
 * Build high-value content section
 * @param {string} baseUrl - Base URL
 * @returns {string} - High-value section
 */
function buildHighValueSection(baseUrl) {
  return `## High-Value Content
- [Products NDJSON](${baseUrl}/products.ndjson) : Streamable NDJSON file with each line as a JSON product entity. (Note: Upload the generated NDJSON file to your server to enable this link.)
- [Product JSON Schema](${baseUrl}/product-schema.json) : JSON Schema definition for validating product data. (Note: Upload the generated schema file to your server to enable this link.)`;
}

/**
 * Build product pages section
 * @param {Array} parsedData - Parsed CSV data
 * @param {string} baseUrl - Base URL
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {string} - Product pages section or empty string
 */
function buildProductSection(parsedData, baseUrl, platform) {
  const productLinks = parsedData
    .map(row => buildProductLink(row, baseUrl, platform))
    .filter(Boolean)
    .join('');

  return productLinks
    ? `## Product Pages\n${productLinks}`
    : '';
}

/**
 * Build a single product link
 * @param {Object} row - CSV row data
 * @param {string} baseUrl - Base URL
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {string} - Product link markdown or empty string
 */
function buildProductLink(row, baseUrl, platform) {
  // Build unified product to get normalized fields
  const unified = buildUnifiedProduct(row, platform, 0);

  let productUrl = '';

  if (unified.path) {
    const normalizedPath = unified.path.startsWith('/') ? unified.path : `/${unified.path}`;
    productUrl = `${baseUrl}${normalizedPath}`;
  } else if (unified.handle) {
    // For Shopify or SHOPLINE without Path, build URL from handle
    productUrl = `${baseUrl}/products/${unified.handle}`;
  }

  if (!productUrl) {return '';}

  // Build description with key product information for LLM understanding
  const descriptionParts = [];

  // Add price if available
  if (unified.price != null && unified.price > 0) {
    descriptionParts.push(`$${unified.price}`);
  }

  // Add stock information
  if (unified.stock !== undefined && unified.stock !== null) {
    descriptionParts.push(`${unified.stock} in stock`);
  }

  // Add brand if available
  if (unified.brand) {
    descriptionParts.push(`by ${unified.brand}`);
  }

  // Add first few tags for semantic context
  if (unified.tags && unified.tags.length > 0) {
    const topTags = unified.tags.slice(0, 3).join(', ');
    descriptionParts.push(topTags);
  }

  // Fallback description if no key information available
  const description = descriptionParts.length > 0
    ? descriptionParts.join(' | ')
    : 'Individual product detail page';

  // Escape markdown special characters in title (brackets, parentheses)
  const safeTitle = (unified.title || 'Product').replace(/\[/g, '\\[').replace(/\]/g, '\\]');

  return `- [${safeTitle}](${productUrl}) : ${description}\n`;
}

/**
 * Build optional resources section
 * @param {string} baseUrl - Base URL
 * @returns {string} - Optional section
 */
function buildOptionalSection(baseUrl) {
  return `## Optional
- [Sitemap](${baseUrl}/sitemap.xml) : Full site sitemap for additional navigation. (Note: Replace with your actual sitemap URL.)
- [Original CSV](${baseUrl}/original.csv) : Source export file for reference. (Note: Upload the original CSV to your server to enable this link.)`;
}
